import './storyManagerColumns.css';

import { ToggleGroup } from '@src/components/common/ToggleGroup';
import { UNASSIGNED_FOLDER_ID } from '@src/stores-v2/FolderStructureStore';
import {
  useFolderStructure,
  useStoryManagerView,
  useUserStoryUploadFlow,
} from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import ChevronDownIcon from '@src/svgs/ChevronDownIcon';
import { handleCopyToClipboard } from '@src/utility/general';
import { motion as fmMotion } from 'framer-motion';
import { observer } from 'mobx-react-lite';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';

import Modal from '../../common/Modal';
import { OrbLoadingState } from '../../common/OrbLoadingState';
import { SIDEBAR_SPRING } from '../../sidebar/sidebarMotion';
import CopyContentModal from '../../stage/CopyContentModal';
import { useStoryNavigation } from '../useStoryNavigation';
import BulkActionBar from './BulkActionBar';
import ConfirmDialog from './ConfirmDialog';
import CreateFolderDialog from './CreateFolderDialog';
import DeleteFolderDialog from './DeleteFolderDialog';
import DetailsPanel, { type DetailsTarget } from './DetailsPanel';
import {
  computeFolderAggregates,
  getStoryDurationSeconds,
} from './folderAggregates';
import FolderBreadcrumb from './FolderBreadcrumb';
import FolderCard from './FolderCard';
import FolderRow from './FolderRow';
import FolderTreeSidebar from './FolderTreeSidebar';
import MoveStoryDialog from './MoveStoryDialog';
import RenameFolderDialog from './RenameFolderDialog';
import { getStoryLastActivityMs, getStoryUploadedMs } from './storyActivity';
import {
  type FolderViewActions,
  StoryFolderViewProvider,
  type StorySelection,
} from './storyFolderViewContext';
import StoryListHeader from './StoryListHeader';
import UnfolderedStoryRow from './UnfolderedStoryRow';
import { buildFolderShareLink, useFolderDeepLink } from './useFolderDeepLink';
import { type ViewMode } from './ViewToggle';

import type { Result, ServiceErrorCode } from '@src/services/common/Result';
import type { FolderMutationOk } from '@src/services/FolderService';
import type { RecentSortMode } from '@src/stores-v2/StoryManagerViewStore';
import type { DashboardStory, Folder } from '@src/types.ts/story';
import type { FC, PointerEvent } from 'react';

export type RecentStoryProps = {
  stories: DashboardStory[];
  resetStoryStates: (storyId: string) => void;
  onStoryDelete: (s: DashboardStory) => void;
  onStoriesDelete: (stories: DashboardStory[]) => void;
  onStoryRename: (
    storyId: DashboardStory['id'],
    newTitle: DashboardStory['title'],
  ) => void;
  initPercentCompleted: (story: DashboardStory) => void;
  isStoryPopulated: (storyId: string) => boolean;
  enableAnalytics: boolean;
};

type Props = {
  showcaseId: string;
  /** Slug of the active showcase, written into shareable deep links. */
  showcaseSlug?: string;
  recentStoryProps?: RecentStoryProps;
};

const StoryManagerFolderView: FC<Props> = observer(
  ({ showcaseId, showcaseSlug, recentStoryProps }) => {
    const videoCreator = useVideoCreatorStore();
    const store = useFolderStructure();
    const { openNewModal } = useUserStoryUploadFlow();
    const [moveDialogStoryIds, setMoveDialogStoryIds] = useState<
      string[] | null
    >(null);
    const [renameStory, setRenameStory] = useState<DashboardStory | null>(null);
    const [deleteStory, setDeleteStory] = useState<DashboardStory | null>(null);
    const [bulkDeleteIds, setBulkDeleteIds] = useState<string[] | null>(null);

    const selectionRef = useRef<StorySelection | null>(null);
    const buildActions = useMemo(
      () =>
        (selection: StorySelection): FolderViewActions => {
          selectionRef.current = selection;
          return {
            openMoveDialog: (storyIds: string[]) => {
              if (storyIds.length === 0) return;
              setMoveDialogStoryIds(storyIds);
            },
            openBulkDeleteDialog: (storyIds: string[]) => {
              if (storyIds.length === 0) return;
              setBulkDeleteIds(storyIds);
            },
          };
        },
      [],
    );

    useEffect(() => {
      // Usually already loaded (or in flight) from the Dashboard.
      void store.loadStructure(showcaseId);
    }, [store, showcaseId]);

    // Keep the current folder in the URL (and vice versa) so any folder can be
    // shared, bookmarked, and restored on refresh.
    const deepLink = useFolderDeepLink({ showcaseId, showcaseSlug });

    // Both folder menus close on click, so an inline "Link Copied" swap would
    // never be seen — the toast is the whole confirmation.
    const handleCopyFolderLink = useCallback(
      async (folder: Folder) => {
        const copied = await handleCopyToClipboard(
          buildFolderShareLink(folder.id, showcaseSlug),
        );
        videoCreator.showToast(
          copied
            ? { state: 'success', message: 'Link copied' }
            : { state: 'error', message: 'Could not copy link' },
        );
      },
      [showcaseSlug, videoCreator],
    );

    // Story Manager view state lives in the store so it survives navigating
    // away and back (the component unmounts on navigation). Read reactively via
    // destructuring — the component is an observer.
    const view = useStoryManagerView();
    const {
      treeVisible,
      treeWidth,
      detailsOpen,
      selectedFolderId,
      selectedStoryId,
      selectedClipId,
      storyExpansionOverrides,
      storySortKey,
      storySortDir,
      recentSortMode,
    } = view;
    const { toggleTree, clearRowSelection, setStoryExpanded } = view;
    const onSortStories = view.toggleSort;

    // View toggle was removed; the Story Manager always uses the grid (card)
    // view. The list branch is kept dormant so the toggle can be restored.
    const [viewMode] = useState<ViewMode>('grid');

    // Manual resize of the open tree: drag its right edge. The store clamps the
    // width live and persists it when the drag ends. `isResizingTree` is a
    // transient in-progress gesture, so it stays local.
    const [isResizingTree, setIsResizingTree] = useState(false);
    const resizeStartRef = useRef<{ x: number; width: number } | null>(null);

    // Story Manager scrolls the document (the list has no bounded inner scroll
    // container), so save/restore the *window* scroll position. Save on window
    // scroll; restore in a layout effect after the (non-virtualized) list is
    // laid out — before paint, so there's no visible jump. This lets the scroll
    // position survive navigating away and back.
    useEffect(() => {
      const onScroll = () => view.setScrollTop(window.scrollY);
      window.addEventListener('scroll', onScroll, { passive: true });
      return () => window.removeEventListener('scroll', onScroll);
    }, [view]);

    useLayoutEffect(() => {
      window.scrollTo(0, view.scrollTop);
    }, [view]);

    const handleResizeStart = (event: PointerEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      resizeStartRef.current = { x: event.clientX, width: treeWidth };
      setIsResizingTree(true);
      event.currentTarget.setPointerCapture(event.pointerId);
    };

    const handleResizeMove = (event: PointerEvent<HTMLDivElement>) => {
      const start = resizeStartRef.current;
      if (!start) return;
      view.setTreeWidth(start.width + (event.clientX - start.x));
    };

    const handleResizeEnd = (event: PointerEvent<HTMLDivElement>) => {
      if (!resizeStartRef.current) return;
      resizeStartRef.current = null;
      setIsResizingTree(false);
      event.currentTarget.releasePointerCapture(event.pointerId);
      view.commitTreeWidth();
    };

    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [renameTarget, setRenameTarget] = useState<Folder | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<Folder | null>(null);

    // The details panel content follows the highlighted story/clip; null shows
    // the empty state.
    const detailsTarget = useMemo<DetailsTarget | null>(() => {
      const list = recentStoryProps?.stories ?? [];
      if (selectedStoryId) {
        const story = list.find(s => s.id === selectedStoryId);
        if (story) return { type: 'story', story };
      }
      if (selectedClipId) {
        for (const story of list) {
          const clip = story.otherVideos?.find(c => c.id === selectedClipId);
          if (clip) return { type: 'clip', clip, story };
        }
      }
      return null;
    }, [recentStoryProps, selectedStoryId, selectedClipId]);

    // Clear row-level selection when the user navigates into a *different*
    // folder. Keyed on an actual currentFolderId change so a selection restored
    // from the view store on return isn't wiped
    const prevFolderIdRef = useRef(store.currentFolderId);
    useEffect(() => {
      if (prevFolderIdRef.current === store.currentFolderId) return;
      prevFolderIdRef.current = store.currentFolderId;
      clearRowSelection();
    }, [store.currentFolderId]);

    const showError = (code: ServiceErrorCode, message: string) => {
      let displayMessage = message;
      if (code === 'PRECONDITION') {
        displayMessage = 'You don’t have access to modify this folder.';
      } else if (code === 'UNAUTHENTICATED') {
        displayMessage = 'Your session expired. Please refresh and try again.';
      } else if (/showcase not found/i.test(message)) {
        // The folders backend doesn't have a record for this showcase.
        displayMessage = 'This showcase doesn’t support folders yet.';
      }
      videoCreator.showToast({ state: 'error', message: displayMessage });
    };

    const showWarnings = (warnings: { code: string; message: string }[]) => {
      if (!warnings.length) return;
      videoCreator.showToast({
        state: 'warning',
        message: warnings.map(w => w.message).join(' · '),
      });
    };

    const handleResult = (
      result: Result<FolderMutationOk>,
      onSuccess: () => void,
    ) => {
      if (!result.ok) {
        showError(result.error.code, result.error.message);
        return;
      }
      showWarnings(result.value.warnings);
      onSuccess();
    };

    const validateName = (
      name: string,
      parentId: string | null,
      ignoreFolderId?: string,
    ): string | null => {
      if (!name) return 'Name is required';
      if (store.hasSiblingNameClash(name, parentId, ignoreFolderId)) {
        return `A folder named "${name}" already exists here`;
      }
      return null;
    };

    const handleCreate = async (name: string) => {
      const result = await store.createFolder(name);
      handleResult(result, () => setIsCreateDialogOpen(false));
    };

    const handleRename = async (folder: Folder, name: string) => {
      const result = await store.renameFolder(folder.id, name);
      handleResult(result, () => setRenameTarget(null));
    };

    const handleDelete = async (
      folder: Folder,
      mode: 'cascade' | 'promote',
    ) => {
      const result = await store.deleteFolder(folder.id, mode);
      handleResult(result, () => setDeleteTarget(null));
    };

    const runMoveStories = async (
      storyIds: string[],
      folderId: string | null,
      onSuccess?: () => void,
    ): Promise<void> => {
      const result = await store.moveStoriesToFolder(storyIds, folderId);
      if (!result.ok) {
        showError(result.error.code, result.error.message);
        return;
      }
      showWarnings(result.value.warnings);
      selectionRef.current?.clear();
      onSuccess?.();
    };

    const handleStoriesDropped = (
      storyIds: string[],
      folderId: string | null,
    ) => runMoveStories(storyIds, folderId);

    const handleMoveDialogSubmit = (folderId: string | null) => {
      if (!moveDialogStoryIds) return;
      return runMoveStories(moveDialogStoryIds, folderId, () =>
        setMoveDialogStoryIds(null),
      );
    };

    const folderAggregates = useMemo(
      () =>
        computeFolderAggregates(
          store.structure,
          recentStoryProps?.stories ?? [],
        ),
      [store.structure, recentStoryProps?.stories],
    );

    // `recentStoryProps.stories` is already filtered to dashboard-visible
    // stories upstream, so a tile's count never disagrees with the list it
    // opens into. The memos below only partition/sort that set.
    const visibleStoriesInCurrentFolder = useMemo(() => {
      const map = store.structure.storyFolderMap;
      // The virtual "Unassigned" folder shows stories with no assignment (the
      // same set the root level would show).
      const target =
        store.currentFolderId === UNASSIGNED_FOLDER_ID
          ? null
          : store.currentFolderId;
      return (recentStoryProps?.stories ?? []).filter(
        s => (map[s.id] ?? null) === target,
      );
    }, [
      recentStoryProps,
      store.structure.storyFolderMap,
      store.currentFolderId,
    ]);

    // Stories with no folder assignment — powers the "Unassigned" tile's stats
    // and cover, independent of the current navigation level.
    const unassignedStories = useMemo(() => {
      const map = store.structure.storyFolderMap;
      return (recentStoryProps?.stories ?? []).filter(
        s => (map[s.id] ?? null) === null,
      );
    }, [recentStoryProps, store.structure.storyFolderMap]);

    // "Recent Stories": every story across every folder — including ones nested
    // in subfolders — ordered by whichever of the two peer sorts is selected.
    // Neither mode narrows the list, so both show the same rows.
    const recentStories = useMemo(() => {
      const rank =
        recentSortMode === 'uploaded'
          ? getStoryUploadedMs
          : getStoryLastActivityMs;
      return [...(recentStoryProps?.stories ?? [])].sort(
        (a, b) => rank(b) - rank(a),
      );
    }, [recentStoryProps, recentSortMode]);

    const [recentLimit, setRecentLimit] = useState(10);

    // Switching sorts re-seeds the list, so an expanded "View More" run would
    // otherwise keep showing rows the new order didn't put there.
    useEffect(() => {
      setRecentLimit(10);
    }, [recentSortMode]);

    const stories = useMemo(() => {
      if (!storySortKey) return visibleStoriesInCurrentFolder;
      const dir = storySortDir === 'asc' ? 1 : -1;
      return [...visibleStoriesInCurrentFolder].sort((a, b) => {
        switch (storySortKey) {
          case 'name':
            return dir * (a.title || '').localeCompare(b.title || '');
          case 'length':
            return (
              dir * (getStoryDurationSeconds(a) - getStoryDurationSeconds(b))
            );
          case 'clips':
            return (
              dir *
              ((a.otherVideos?.length ?? 0) - (b.otherVideos?.length ?? 0))
            );
          case 'lastUpdate':
            // "Last activity" — the same clip-folding value the rows display.
            return (
              dir * (getStoryLastActivityMs(a) - getStoryLastActivityMs(b))
            );
        }
      });
    }, [visibleStoriesInCurrentFolder, storySortKey, storySortDir]);

    // Folders keep their natural order. The list header sorts the *story* list
    // ("Story Name", etc.) — reordering stories must not reorder folders.
    const subfolders = store.currentSubfolders;

    // Navigation modes, all keyed off the single `currentFolderId`.
    const isRoot = store.currentFolderId === null;
    const isUnassignedView = store.currentFolderId === UNASSIGNED_FOLDER_ID;
    const isRealFolder = !isRoot && !isUnassignedView;
    const hasRealFolders = subfolders.length > 0;

    // At root, unfoldered stories collapse into an "Unassigned" tile — but only
    // once real folders exist. With no folders yet there's nothing to organize
    // around, so we keep the plain flat story list (legacy first-run behavior).
    const showUnassignedTile =
      isRoot && hasRealFolders && unassignedStories.length > 0;
    const showFlatRootList =
      isRoot && !hasRealFolders && unassignedStories.length > 0;
    const showRecentStories = isRoot && hasRealFolders;
    const showFolderStoryList =
      !!recentStoryProps &&
      stories.length > 0 &&
      (isRealFolder || isUnassignedView || showFlatRootList);

    const { openStory, openClip } = useStoryNavigation(
      recentStoryProps?.resetStoryStates,
    );

    const unassignedFolder: Folder = {
      id: UNASSIGNED_FOLDER_ID,
      name: 'Unassigned',
      parentId: null,
      position: -1,
      createdAt: '',
    };

    const renderStoryRow = (story: DashboardStory) => (
      <UnfolderedStoryRow
        key={story.id}
        story={story}
        isSelected={selectedStoryId === story.id}
        onSelect={s => view.selectStory(s.id)}
        onOpen={openStory}
        onRename={s => setRenameStory(s)}
        onDelete={s => setDeleteStory(s)}
        onRetry={s => {
          recentStoryProps?.onStoryDelete(s);
          openNewModal();
        }}
        onRemoveFailed={s => recentStoryProps?.onStoryDelete(s)}
        isExpanded={storyExpansionOverrides.get(story.id)}
        onToggleExpand={setStoryExpanded}
        selectedClipId={selectedClipId}
        onSelectClip={clip => view.selectClip(clip.id ?? null)}
        onOpenClip={clip => openClip(clip, story)}
        onDetails={s => {
          view.selectStory(s.id);
          view.setDetailsOpen(true);
        }}
        onClipDetails={clip => {
          view.selectClip(clip.id ?? null);
          view.setDetailsOpen(true);
        }}
      />
    );

    // Same orb and copy as the Dashboard's story-list wait, so a deep link
    // reads as one wait rather than a root page that then jumps.
    if (deepLink.pending) return <OrbLoadingState text="Loading…" />;

    return (
      <StoryFolderViewProvider buildActions={buildActions}>
        <OuterWrapper $resizing={isResizingTree}>
          <TreePanel
            initial={false}
            animate={
              treeVisible
                ? {
                    width: treeWidth,
                    marginRight: 24,
                    opacity: 1,
                    visibility: 'visible',
                  }
                : {
                    width: 0,
                    marginRight: 0,
                    opacity: 0,
                    transitionEnd: { visibility: 'hidden' },
                  }
            }
            // No spring while dragging — width must track the cursor 1:1.
            transition={isResizingTree ? { duration: 0 } : SIDEBAR_SPRING}
            aria-hidden={!treeVisible}
          >
            <FolderTreeSidebar
              width={treeWidth}
              onStoriesDropped={handleStoriesDropped}
            />
            {treeVisible && (
              <ResizeHandle
                onPointerDown={handleResizeStart}
                onPointerMove={handleResizeMove}
                onPointerUp={handleResizeEnd}
                data-active={isResizingTree || undefined}
                role="separator"
                aria-orientation="vertical"
                aria-label="Resize folder tree"
              />
            )}
          </TreePanel>

          <MainPanel data-sm-panel="true">
            <BulkActionBar />

            <MainContent
              onClick={() => {
                // Clicking empty space in the content area clears row highlight.
                // Rows stopPropagation on their own click so this only fires
                // when the click misses every folder/story/clip row.
                clearRowSelection();
              }}
            >
              <BreadcrumbBar>
                <TreeToggleButton
                  type="button"
                  onClick={event => {
                    event.stopPropagation();
                    toggleTree();
                  }}
                  $active={treeVisible}
                  aria-label={
                    treeVisible ? 'Hide folder tree' : 'Show folder tree'
                  }
                  title={treeVisible ? 'Hide folder tree' : 'Show folder tree'}
                  data-testid="folder-tree-toggle"
                >
                  <ChevronDownIcon width="14" height="14" />
                </TreeToggleButton>
                <FolderBreadcrumb
                  trail={store.breadcrumb}
                  onNavigate={folderId =>
                    folderId == null
                      ? store.goToRoot()
                      : store.enterFolder(folderId)
                  }
                  onStoriesDropped={handleStoriesDropped}
                  // The virtual "Unassigned" folder isn't editable — omit the
                  // breadcrumb actions menu there so the current crumb renders
                  // as plain text (matching the tile's `hideMenu`).
                  onCopyFolderLink={
                    isUnassignedView ? undefined : handleCopyFolderLink
                  }
                  onRenameFolder={
                    isUnassignedView
                      ? undefined
                      : target => setRenameTarget(target)
                  }
                  onDeleteFolder={
                    isUnassignedView
                      ? undefined
                      : target => setDeleteTarget(target)
                  }
                  onNewSubfolder={
                    isUnassignedView
                      ? undefined
                      : () => setIsCreateDialogOpen(true)
                  }
                />
              </BreadcrumbBar>

              {!isUnassignedView && <SectionLabel>Folders</SectionLabel>}

              {!isUnassignedView &&
                (viewMode === 'grid' ? (
                  <FolderGrid>
                    {showUnassignedTile && (
                      <FolderCard
                        key={UNASSIGNED_FOLDER_ID}
                        folder={unassignedFolder}
                        aggregate={folderAggregates.get(UNASSIGNED_FOLDER_ID)}
                        previewImageUrl={
                          folderAggregates.get(UNASSIGNED_FOLDER_ID)
                            ?.coverThumbnailUrl
                        }
                        hideMenu
                        onOpen={() => store.enterUnassigned()}
                        onCopyLink={() => {}}
                        onRename={() => {}}
                        onDelete={() => {}}
                      />
                    )}
                    {subfolders.map(folder => (
                      <FolderCard
                        key={folder.id}
                        folder={folder}
                        aggregate={folderAggregates.get(folder.id)}
                        previewImageUrl={
                          folderAggregates.get(folder.id)?.coverThumbnailUrl
                        }
                        onOpen={id => store.enterFolder(id)}
                        onCopyLink={handleCopyFolderLink}
                        onRename={target => setRenameTarget(target)}
                        onDelete={target => setDeleteTarget(target)}
                        onStoriesDropped={(ids, fid) =>
                          handleStoriesDropped(ids, fid)
                        }
                      />
                    ))}
                    {/* "Add here" card — matches the folder tiles. */}
                    <NewFolderCard
                      type="button"
                      onClick={() => setIsCreateDialogOpen(true)}
                      data-testid="new-folder-button"
                    >
                      <NewFolderPlus>+</NewFolderPlus>
                      New Folder
                    </NewFolderCard>
                  </FolderGrid>
                ) : (
                  <>
                    {subfolders.map(folder => {
                      const agg = folderAggregates.get(folder.id);
                      return (
                        <FolderRow
                          key={folder.id}
                          folder={folder}
                          subfolderCount={store.subfolderCountForFolder(
                            folder.id,
                          )}
                          storyCount={store.storyCountForFolder(folder.id)}
                          aggregateStoryCount={agg?.storyCount}
                          aggregateClipCount={agg?.clipCount}
                          aggregateDurationSeconds={agg?.durationSeconds}
                          aggregateLastUpdate={agg?.lastUpdate}
                          isSelected={selectedFolderId === folder.id}
                          onSelect={id => view.selectFolder(id)}
                          onOpen={id => store.enterFolder(id)}
                          onCopyLink={handleCopyFolderLink}
                          onRename={target => setRenameTarget(target)}
                          onDelete={target => setDeleteTarget(target)}
                          onStoriesDropped={(ids, fid) =>
                            handleStoriesDropped(ids, fid)
                          }
                        />
                      );
                    })}
                    <NewFolderButton
                      type="button"
                      onClick={() => setIsCreateDialogOpen(true)}
                    >
                      + New Folder
                    </NewFolderButton>
                  </>
                ))}

              {showFolderStoryList && (
                <>
                  <SectionLabel>
                    {isUnassignedView ? 'Unassigned' : 'Stories & Clips'}
                  </SectionLabel>
                  <StoryListHeader
                    sortKey={storySortKey}
                    sortDir={storySortDir}
                    onSort={onSortStories}
                    detailsOpen={detailsOpen}
                    onToggleDetails={() => view.setDetailsOpen(!detailsOpen)}
                  />
                  {stories.map(renderStoryRow)}
                </>
              )}

              {showRecentStories && recentStories.length > 0 && (
                <>
                  <RecentHeader>
                    <SectionLabel>Recent Stories</SectionLabel>
                    {/* Peer sorts over one set, not a filter — so `neutral`,
                        and no counts (both segments show every story, so the
                        two numbers would always agree and say nothing). */}
                    <ToggleGroup<RecentSortMode>
                      options={[
                        { id: 'activity', label: 'Recent Activity' },
                        { id: 'uploaded', label: 'Recently Uploaded' },
                      ]}
                      value={recentSortMode}
                      onChange={view.setRecentSortMode}
                      ariaLabel="Recent stories order"
                      testIdPrefix="story-manager-recent-sort"
                    />
                  </RecentHeader>
                  {recentStories.slice(0, recentLimit).map(renderStoryRow)}
                  {recentLimit < recentStories.length && (
                    <ViewMoreButton
                      type="button"
                      onClick={() => setRecentLimit(n => n + 10)}
                    >
                      View More ({recentStories.length - recentLimit} remaining)
                    </ViewMoreButton>
                  )}
                </>
              )}
            </MainContent>
          </MainPanel>
        </OuterWrapper>

        <DetailsPanel
          isOpen={detailsOpen}
          target={detailsTarget}
          onClose={() => view.setDetailsOpen(false)}
        />

        {isCreateDialogOpen && (
          <CreateFolderDialog
            validate={name => validateName(name, store.currentFolderId)}
            onSubmit={handleCreate}
            onClose={() => setIsCreateDialogOpen(false)}
          />
        )}

        {renameTarget && (
          <RenameFolderDialog
            folder={renameTarget}
            validate={name =>
              validateName(name, renameTarget.parentId, renameTarget.id)
            }
            onSubmit={name => handleRename(renameTarget, name)}
            onClose={() => setRenameTarget(null)}
          />
        )}

        {deleteTarget && (
          <DeleteFolderDialog
            folder={deleteTarget}
            subfolderCount={store.subfolderCountForFolder(deleteTarget.id)}
            storyCount={store.storyCountForFolder(deleteTarget.id)}
            onConfirm={mode => handleDelete(deleteTarget, mode)}
            onClose={() => setDeleteTarget(null)}
          />
        )}

        {moveDialogStoryIds && (
          <MoveStoryDialog
            storyIds={moveDialogStoryIds}
            title={
              moveDialogStoryIds.length === 1 && recentStoryProps
                ? recentStoryProps.stories.find(
                    s => s.id === moveDialogStoryIds[0],
                  )?.title
                : undefined
            }
            onSubmit={handleMoveDialogSubmit}
            onClose={() => setMoveDialogStoryIds(null)}
          />
        )}

        {renameStory && recentStoryProps && (
          <Modal isOpen closeModal={() => setRenameStory(null)}>
            <CopyContentModal
              name={renameStory.title}
              tag="Story"
              type="rename"
              onCancel={() => setRenameStory(null)}
              onSave={(newTitle: string) => {
                recentStoryProps.onStoryRename(renameStory.id, newTitle);
                setRenameStory(null);
              }}
            />
          </Modal>
        )}

        {deleteStory && recentStoryProps && (
          <ConfirmDialog
            title="Delete this story?"
            description="This cannot be undone."
            confirmLabel="Delete"
            destructive
            onConfirm={() => {
              recentStoryProps.onStoryDelete(deleteStory);
              setDeleteStory(null);
            }}
            onClose={() => setDeleteStory(null)}
          />
        )}

        {bulkDeleteIds && recentStoryProps && (
          <ConfirmDialog
            title={`Delete ${bulkDeleteIds.length} ${
              bulkDeleteIds.length === 1 ? 'story' : 'stories'
            }?`}
            description="This cannot be undone."
            confirmLabel={`Delete ${bulkDeleteIds.length} ${
              bulkDeleteIds.length === 1 ? 'story' : 'stories'
            }`}
            destructive
            onConfirm={() => {
              const idSet = new Set(bulkDeleteIds);
              const storiesToDelete = recentStoryProps.stories.filter(s =>
                idSet.has(s.id),
              );
              recentStoryProps.onStoriesDelete(storiesToDelete);
              selectionRef.current?.clear();
              setBulkDeleteIds(null);
            }}
            onClose={() => setBulkDeleteIds(null)}
          />
        )}
      </StoryFolderViewProvider>
    );
  },
);

export default StoryManagerFolderView;

// ─── Styles ─────────────────────────────────────────────────────────────────

const OuterWrapper = styled.div<{ $resizing?: boolean }>`
  display: flex;
  flex-direction: row;
  flex: 1;
  min-height: 0;
  /* While dragging the tree edge: kill text selection and force the resize
     cursor everywhere so it doesn't flicker as the pointer crosses elements. */
  ${({ $resizing }) =>
    $resizing &&
    `
    cursor: col-resize;
    user-select: none;
  `}
  /* No gap: the tree panel animates its own margin-right (a flex gap would
     leave a 24px gutter while the always-mounted panel is collapsed to 0). */
  /* Sidebar panel and folder grid both start here — 40px below the header. */
  padding-top: 40px;
  overflow: hidden;
`;

// Slide-in wrapper for the opt-in folder tree. Mirrors the main side menu:
// framer-motion width spring (SIDEBAR_SPRING), clipping the fixed-width
// sidebar while it glides open/closed.
const TreePanel = styled(fmMotion.div)`
  position: relative;
  display: flex;
  flex-shrink: 0;
  overflow: hidden;
`;

// Drag the tree's right edge to resize it (between TREE_MIN/MAX_WIDTH).
const ResizeHandle = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  width: 8px;
  cursor: col-resize;
  z-index: 2;
  touch-action: none;

  /* Thin grabber line at the very edge; brightens on hover / while dragging. */
  &::after {
    content: '';
    position: absolute;
    top: 8px;
    bottom: 8px;
    right: 1px;
    width: 2px;
    border-radius: 2px;
    background: transparent;
    transition: background 120ms ease;
  }

  &:hover::after,
  &[data-active]::after {
    background: var(--text-muted);
  }
`;

const MainPanel = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  overflow: hidden;
`;

const MainContent = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  /* No top padding: the folder grid's first row top-aligns with the sidebar
     panel (the 40px gap below the header comes from OuterWrapper). */
  padding-bottom: var(--spacing-md);
`;

const BreadcrumbBar = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-md);
`;

// Section headers for the folder grid and story list. Matches the homepage
// "Recent Content" header (RecentContent.tsx SectionHeader): 20px + the same
// weight token, so the three read consistently across the app.
const SectionLabel = styled.h2`
  margin: 0;
  margin-top: var(--spacing-md);
  padding: 0 var(--spacing-md) var(--spacing-sm);
  font-family: var(--font-primary);
  font-size: var(--text-md);
  font-weight: var(--font-weight-semibold);
  color: var(--foreground);
`;

// "Recent Stories" heading and its order toggle on one line, wrapping rather
// than crushing the control on a narrow viewport.
const RecentHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: var(--spacing-sm);
  padding-right: var(--spacing-md);

  /* The row takes over the heading's block spacing. Left on the heading, its
     margin-top would be part of that flex item's outer box, so centring the line
     would sit the heading text below the toggle rather than level with it. */
  margin-top: var(--spacing-md);
  padding-bottom: var(--spacing-sm);
  ${SectionLabel} {
    margin-top: 0;
    padding-bottom: 0;
  }

  /* ToggleGroup sets align-self: flex-start so a column-flex host cannot
     stretch its track; in this row we want it centred on the heading. */
  > * {
    align-self: center;
  }
`;

// Full-width "View More" button under the Recent Stories list — expands the
// list in place. Mirrors the homepage Recent Content affordance
// (RecentContent.tsx ViewMoreButton): centered label, full width.
const ViewMoreButton = styled.button`
  align-self: stretch;
  margin: 16px var(--spacing-md) var(--spacing-md);
  padding: 12px 24px;
  border-radius: 6px;
  font-family: var(--font-primary);
  font-size: 13px;
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  border: 1px solid var(--border);
  background-color: transparent;
  color: var(--text-muted);
  transition: all var(--transition-fast) ease;

  &:hover {
    background-color: var(--surface);
    border-color: var(--text-muted);
    color: var(--text-secondary);
  }
`;

// Ghost toggle for the opt-in folder tree (breadcrumbs are the primary nav).
const TreeToggleButton = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 4px;
  background: ${({ $active }) =>
    $active ? 'var(--surface-elevated)' : 'none'};
  border: none;
  border-radius: var(--radius-button);
  color: ${({ $active }) =>
    $active ? 'var(--foreground)' : 'var(--text-muted)'};
  cursor: pointer;

  /* Directional chevron: points right (>) to expand when closed, left (<) to
     collapse when open. */
  svg {
    transition: transform 150ms ease;
    transform: rotate(${({ $active }) => ($active ? '90deg' : '-90deg')});
  }

  &:hover {
    background: var(--border);
    color: var(--text-secondary);
  }
`;

const FolderGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 12px;
  padding: 0 var(--spacing-md) var(--spacing-md);
`;

// Soft "add here" tile that matches the folder cards in the grid. Kept gentle:
// a hairline dashed border and a friendly rounded "+" chip rather than a stark
// outline, warming up on hover.
const NewFolderCard = styled.button`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm);
  border: 1px dashed var(--border-subtle);
  border-radius: var(--radius-card);
  background: transparent;
  color: var(--text-muted);
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  text-align: left;
  transition:
    background 150ms ease,
    border-color 150ms ease,
    color 150ms ease;

  &:hover {
    background: var(--surface);
    border-color: var(--border);
    color: var(--text-secondary);
  }
`;

// Mirrors the folder card's thumbnail box (84×48) so the "+" lines up with the
// folder cover thumbnails in the same column.
const NewFolderPlus = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 84px;
  height: 48px;
  flex-shrink: 0;
  border-radius: 6px;
  background: var(--surface);
  color: var(--text-secondary);
  font-size: 22px;
  font-weight: var(--font-weight-regular);
  line-height: 1;
  transition:
    background 150ms ease,
    color 150ms ease;

  ${NewFolderCard}:hover & {
    background: var(--surface-elevated);
    color: var(--text-primary);
  }
`;

// A full-width "add here" affordance rendered directly below the folder list.
// Left padding matches the folder rows so the label lines up under the folder
// names rather than floating in a pinned footer.
const NewFolderButton = styled.button`
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  width: 100%;
  background: none;
  border: none;
  padding: 12px var(--spacing-md);
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-medium);
  color: var(--text-muted);
  cursor: pointer;
  text-align: left;
  transition:
    background 120ms ease,
    color 120ms ease;

  &:hover {
    background: var(--surface);
    color: var(--text-primary);
  }
`;
